import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Globe } from "lucide-react";
import { Theme } from "../types/theme";

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lang: string) => void;
  initialLang?: string;
  theme?: Theme;
}

const LanguageModal: React.FC<LanguageModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialLang = "en",
  theme,
}) => {
  const [selected, setSelected] = useState(initialLang);

  useEffect(() => {
    setSelected(initialLang);
  }, [initialLang, isOpen]);

  const hexToRgba = (hex: string, alpha = 1) => {
    try {
      const h = hex.replace("#", "");
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } catch {
      return `rgba(59,130,246,${alpha})`; // fallback (blue)
    }
  };

  const primary = theme?.primaryColor ?? "#3b82f6";
  const mode = theme?.mode ?? "light";

  const saveButtonStyle: React.CSSProperties = {
    background: `linear-gradient(135deg, ${primary} 0%, ${hexToRgba(primary, 0.85)} 100%)`,
    color: "#fff",
    border: "none",
    boxShadow: `0 6px 18px ${hexToRgba(primary, 0.28)}`,
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
  };

  const cancelButtonStyle: React.CSSProperties = {
    background: mode === "dark" ? hexToRgba("#000000", 0.45) : "transparent",
    color: mode === "dark" ? "#fff" : hexToRgba(primary, 1),
    border: `1px solid ${hexToRgba(primary, 0.18)}`,
    padding: "8px 12px",
    borderRadius: 10,
    cursor: "pointer",
  };

  const modalBg =
    mode === "dark"
      ? "linear-gradient(180deg, rgba(20,20,20,0.9), rgba(10,10,10,0.85))"
      : "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(245,245,245,0.95))";

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-6 w-[90%] max-w-md"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 25 }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <X size={20} />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <Globe className="w-12 h-12 text-indigo-600" />
            </div>

            <h2 className="text-xl font-bold text-center mb-6">
              Select Your Language
            </h2>

            {/* Dropdown */}
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-white p-3 pr-8 text-gray-700 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="zh">中文</option>
            </select>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                style={cancelButtonStyle}
                aria-label="Cancel"
              >
                Cancel
              </button>

              <button
                onClick={() => onSave(selected)}
                style={saveButtonStyle}
                aria-label="Save"
              >
                Save
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LanguageModal;
